import React, { useMemo, useContext, Fragment, forwardRef } from 'react';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  IconButton,
  Text,
} from '@fluentui/react';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SurveyContextType } from '../../../survey-context';
import { closeButtonStyles, backButtonStyles, theme } from '../../../styles';
import { I18n } from '../../../survey.types';

const EndingPageHeader = forwardRef((props: {
  onDone: any,
  classes: any,
}, ref: any): JSX.Element => {
  const {
    onDone,
    classes,
  } = props;

  const i18n: I18n = useContext(I18nContext);

  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getPageIndex,
  } = surveyContext;

  return (
    <Fragment>
      <Text id="SurveyTitle" className={classNames(classes.title, 'survey-title')} role="heading" aria-level={3}>
        {i18n.getString(_TL_('Thank you for your feedback'))}
      </Text>
      <div className={classNames(classes.doneIconButton, 'survey-done-icon-button')}>
        <IconButton
          styles={closeButtonStyles}
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel={i18n.getString(_TL_('Close survey'))}
          onClick={() => {
            onDone();
            logActivity(`page: ${getPageIndex()}, crossOutButton: done`);
          }}
          componentRef={ref}
        />
      </div>
    </Fragment>
  );
});

const QuestionHeader = forwardRef((props: {
  onBack: any,
  onDismiss: any,
  isMiddlePage: boolean,
  classes: any,
}, ref: any): JSX.Element => {
  const {
    onBack,
    onDismiss,
    isMiddlePage,
    classes,
  } = props;

  const i18n: I18n = useContext(I18nContext);

  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getPageIndex,
  } = surveyContext;

  return (
    <Fragment>
      <div className={classNames(classes.backIconButton, 'survey-back-icon-button')}>
        {isMiddlePage &&
          <IconButton
            styles={backButtonStyles}
            iconProps={{ iconName: 'Back' }}
            ariaLabel={i18n.getString(_TL_('Back to the previous page'))}
            onClick={() => { onBack(); logActivity(`page: ${getPageIndex()}, backButton: back`); }}
            componentRef={ref}
          />
        }
        <Text id="SurveyTitle" className={classNames(classes.title, 'survey-title')} role="heading" aria-level={3}>
          {i18n.getString(_TL_('Feedback to Microsoft'))}
        </Text>
      </div>
      <div className={classNames(classes.closeIconButton, 'survey-close-icon-button')}>
        <IconButton
          className={classNames('survey-close-icon-button')}
          styles={closeButtonStyles}
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel={i18n.getString(_TL_('Close survey'))}
          onClick={() => { onDismiss('icon'); logActivity(`page: ${getPageIndex()}, crossOutButton: dismiss`); }}
          componentRef={isMiddlePage ? undefined : ref}
        />
      </div>
    </Fragment>
  );
});

interface HeaderProps {
  isFirstPage: boolean;
  isEndingPage: boolean;
  classes: any;
  onDismiss: any;
  onBack: any;
  onDone: any;
}

export const Header = forwardRef((props: HeaderProps, ref): JSX.Element => {
  const {
    isFirstPage,
    isEndingPage,
  } = props;

  const classes: any = useDefaultStyles(() => ({
    title: {
      fontSize: '16px',
      color: theme.palette.black,
      fontWeight: 600,
    },
    header: {
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& button': {
        padding: 'unset',
        marginTop: 'unset',
      },
      marginBottom: '8px',
    },
    backIconButton: {
      display: 'flex',
      alignItems: 'center',
      '& button.ms-Button': {
        paddingLeft: 0,
        marginLeft: 0,
        width: 'unset',
      },
      '& button.ms-Button:hover': {
        color: 'unset',
        backgroundColor: 'unset',
      },
      '& button.ms-Button i': {
        paddingLeft: 0,
        marginLeft: 0,
      },
    },
    closeIconButton: {
      '& button.ms-Button': {
        paddingRight: 0,
        marginRight: 0,
        width: 'unset',
      },
      '& button.ms-Button i': {
        paddingRight: 0,
        marginRight: 0,
      },
      '& button.ms-Button:hover': {
        color: 'unset',
        backgroundColor: 'unset',
      },
    },
    doneIconButton: {
      '& button.ms-Button': {
        paddingRight: 0,
        marginRight: 0,
        width: 'unset',
      },
      '& button.ms-Button i': {
        paddingRight: 0,
        marginRight: 0,
      },
      '& button.ms-Button:hover': {
        color: 'unset',
        backgroundColor: 'unset',
      },
    },
  }));

  const isMiddlePage = useMemo(() =>
    !isFirstPage && !isEndingPage, [isFirstPage, isEndingPage]);

  return (
    <div className={classNames(classes.header, 'survey-header')} >
      {isEndingPage ?
        <EndingPageHeader {...props} classes={classes} ref={ref} /> :
        <QuestionHeader {...props} isMiddlePage={isMiddlePage} classes={classes} ref={ref} />}
    </div>
  );
});
